import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/visa-guides-2026";
const TITLE = "Visa Guides 2026 | LifeWestWay";

export const Route = createFileRoute("/visa-guides-2026")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content:
          makeSeoDescription("The four essential 2026 immigration guides: Portugal D7 vs D8, Canada Express Entry CRS, UK Skilled Worker, and Australia Subclass 189/190/482 — updated thresholds, rules and expert analysis."),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("One hub. Four countries. The latest 2026 visa rules, salary floors and pathways decoded."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: VisaGuides2026Page,
});

type Guide = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  to: string;
  params: Record<string, string>;
};

const GUIDES: Guide[] = [
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-rose-500/10",
    title: "D7 vs Digital Nomad Visa (D8) — 2026",
    blurb:
      "Passive income vs active remote work. Compare the €920/mo D7 route with the €3,680/mo D8 and pick the one that actually fits your life.",
    highlight: "€920 vs €3,680 income floors",
    to: "/portugal/$slug",
    params: { slug: "d7-vs-digital-nomad-visa-portugal-2026" },
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "Express Entry — CRS Score & Draws 2026",
    blurb:
      "Job-offer points are gone, work experience thresholds doubled, and five new category draws are live. Here's the strategic pivot for 2026.",
    highlight: "5 new category-based draws",
    to: "/canada/$slug",
    params: { slug: "express-entry-crs-score-draws-2026" },
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "UK Skilled Worker Visa — 2026 Guide",
    blurb:
      "RQF Level 6 skill floor, £41,700 general salary threshold and rolling 3-month payroll audits. The full compliance playbook.",
    highlight: "£41,700 salary threshold",
    to: "/uk/$slug",
    params: { slug: "uk-skilled-worker-visa-2026-guide" },
  },
  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "Subclass 189 vs 190 vs 482 — 2026",
    blurb:
      "Points-tested PR, state-nominated pathways and the rebranded Skills in Demand visa with Core/Specialist streams ($76k–$141k+).",
    highlight: "2-year fast-track to PR",
    to: "/australia/$slug",
    params: { slug: "subclass-189-190-482-australian-visa-comparison-2026" },
  },
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-blue-500/25 to-red-400/10",
    title: "H-1B Visa 2026 — Process & Timeline",
    blurb:
      "Registration windows, the $215 lottery fee, cap-exempt employers and the day-by-day timeline from selection to an October start date.",
    highlight: "Lottery, LCA & premium processing",
    to: "/usa/$slug",
    params: { slug: "h1b-visa-2026-process-timeline-checklist" },
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "Italy Elective Residency Visa — 2026",
    blurb:
      "The passive-income route for retirees and financially independent movers: €31,000+ proof of funds, housing rules and permesso di soggiorno steps.",
    highlight: "€31,000+ passive income",
    to: "/italy/$slug",
    params: { slug: "italy-elective-residency-visa-2026-guide" },
  },
];


function VisaGuides2026Page() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "2026 Visa Guides" },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            Curated · Updated 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            The 2026 <span className="text-accent">Visa Guides</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-foreground/75">
            Four countries. Four decisive 2026 rule changes. One place to compare thresholds,
            pathways and the strategic moves that matter this year.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section aria-label="Featured 2026 visa guides" className="mt-10 grid gap-6 md:grid-cols-2">
        {GUIDES.map((g) => (
          <Link title={`Read the 2026 visa guide: ${g.title}`}
            key={g.title}
            to={g.to}
            params={g.params}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${g.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {g.flag}
                </span>
                {g.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                2026
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {g.title}
            </h2>
            <p className="relative mt-3 text-sm sm:text-base text-foreground/70">{g.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {g.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Read guide <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL["visa-guides-2026"]} />

      <ShareBar />
    </main>
  );
}
